import { useCampaignPopup } from "@hooks/queries"
import { cn } from "@utils/tw"
import { ResponsiveImage } from "./ResponsiveImage"
import { Typography } from "./Typography"
import { CLOSE } from "./Icons"
import { ComponentProps, ComponentPropsWithoutRef, forwardRef, useEffect, useState } from "react"
import { Link } from "eddev/routing"
import { motion, AnimatePresence } from "framer-motion"
import { useIsMobile } from "@hooks/useViewportDerived"

const DISMISS_REMEMBER_TIME = 3600 * 24 * 3 * 1000
const POPUP_DELAY_MS = 8000

export function CampaignPopup() {
  const data = useCampaignPopup()
  const [showPopup, setShowPopup] = useState<boolean>(false)
  const isMobile = useIsMobile()

  const campaignUrl = data?.data?.siteSettings?.themeSiteSettings?.campaignLink?.url
  const campaignPopupKey = campaignUrl ? `dismissedCampaignPopup_${campaignUrl}` : "default"

  const getHasDismissed = () => {
    const value = localStorage.getItem(campaignPopupKey)
    if (!value) return false
    const date = new Date(value)
    const now = new Date()
    return now.getTime() - date.getTime() < DISMISS_REMEMBER_TIME
  }
  const setHasDismissed = () => localStorage.setItem(campaignPopupKey, new Date().toISOString())

  useEffect(() => {
    if (!getHasDismissed()) {
      const timer = setTimeout(() => {
        setShowPopup(true)
      }, POPUP_DELAY_MS)
      return () => clearTimeout(timer)
    }
  }, [campaignPopupKey])

  if (data.data?.siteSettings?.themeSiteSettings?.campaignShowPopup) {
    return (
      <AnimatePresence>
        {showPopup && (
          <PopupContainer
            initial={{ opacity: 0, y: 20, x: isMobile ? "-50%" : 0 }}
            animate={{ opacity: 1, y: 0, x: isMobile ? "-50%" : 0 }}
            exit={{ opacity: 0, y: 0, x: isMobile ? "-50%" : 0 }}
          >
            <FloatingLink
              href={data.data.siteSettings.themeSiteSettings.campaignLink?.url}
              target={data.data.siteSettings.themeSiteSettings.campaignLink?.target ? "_blank" : undefined}
            />
            {data.data.siteSettings.themeSiteSettings?.campaignThumbnail && (
              <ImageContainer>
                <ResponsiveImage {...data.data.siteSettings.themeSiteSettings.campaignThumbnail} aspect={1} />
              </ImageContainer>
            )}
            <Content>
              <Typography as="span" variant="h5">
                {data.data.siteSettings.themeSiteSettings.campaignLine1}
              </Typography>
              <Typography as="span" variant="text-m">
                {data.data.siteSettings.themeSiteSettings.campaignLink?.title}
              </Typography>
            </Content>
            <button
              onClick={() => {
                setHasDismissed()
                setShowPopup(false)
              }}
            >
              {CLOSE}
            </button>
          </PopupContainer>
        )}
      </AnimatePresence>
    )
  }

  return null
}

const PopupContainer = forwardRef<HTMLDivElement, ComponentProps<typeof motion.div>>(({ className, ...props }, ref) => (
  <motion.div
    ref={ref}
    className={cn(
      "fixed bottom-[95px] z-20 left-1/2 max-w-grid bg-black w-full p-3 rounded-md border border-white/20",
      "shadow-[0px_6px_12px_0px_#22222214,0px_1px_3px_0px_#2222221A] flex gap-3 text-white",
      "transition-colors ease-movement duration-300",
      "[&_button]:z-[4] [&_button]:min-w-[16px] [&_button]:absolute [&_button]:right-3 [&_button]:h-full",
      "[&_button]:flex [&_button]:items-center [&_button]:appearance-none [&_button]:bg-transparent [&_button]:border-none [&_button]:top-0",
      "hover:text-black hover:bg-yellow",
      "sm:w-auto",
      "md:bottom-[50px] md:transform-none md:left-auto md:right-[3vw]",
      "xxl:right-[50px]",
      className,
    )}
    {...props}
  />
))
PopupContainer.displayName = "PopupContainer"

const FloatingLink = forwardRef<HTMLAnchorElement, ComponentProps<typeof Link>>(({ className, ...props }, ref) => (
  <Link ref={ref} className={cn("absolute inset-0 z-[2]", className)} {...props} />
))
FloatingLink.displayName = "FloatingLink"

const ImageContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("relative overflow-hidden bg-white rounded-[6px] w-[46px] h-[46px] min-w-[46px]", className)}
    {...props}
  />
))
ImageContainer.displayName = "ImageContainer"

const Content = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col grow pr-[24px] md:pr-10 md:[&_span]:whitespace-nowrap", className)}
    {...props}
  />
))
Content.displayName = "Content"
